
import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import L from 'leaflet';
import {
    MapContainer as LeafletMapContainer,
    TileLayer,
    Marker,
    Polyline,
    CircleMarker,
    Popup,
    useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import koKr from '../../language/Ko-kr.json';
import enEng from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const parseDate = (val: any) => {
    if (!val) return 0;
    if (Array.isArray(val)) {
        // Array from Spring: [year, month(1-based), day, hour, min, sec]
        // Treat as KST (local time)
        return new Date(val[0], val[1]-1, val[2], val[3]||0, val[4]||0, val[5]||0).getTime();
    }
    let s = String(val).replace(' ', 'T');
    // If no timezone info, DB stores KST → append +09:00
    if (!s.endsWith('Z') && !s.includes('+')) s += '+09:00';
    return new Date(s).getTime();
};

const isKorean = true; // TODO: 실제 언어 설정 연동
const lang = isKorean ? koKr : enEng;

// DB의 origin / destination 값과 1:1 대응
// TODO: DB 연결 시 API 호출로 교체하세요.

interface LocationOption {
    readonly id: string;
    readonly name: string;       // DB 저장 값 (영문)
    readonly nameKo: string;     // 화면 표시용 한글
    readonly coordinates: [number, number]; // [longitude, latitude] — 대표 좌표
    readonly color: string;
}

interface Delivery {
    readonly days: number;
    readonly currentLocation: string;
}

/** DB location 값 목록 (보낸 나라 / 받는 지역 통합) */
const LOCATION_OPTIONS: LocationOption[] = [
    { id: 'africa', name: 'Africa', nameKo: lang.box.locations.africa, coordinates: [20.000, 5.000], color: '#3aaa9e' },
    { id: 'east-asia', name: 'East Asia', nameKo: lang.box.locations.eastAsia, coordinates: [115.000, 35.000], color: '#3aaa9e' },
    { id: 'europe', name: 'Europe', nameKo: lang.box.locations.europe, coordinates: [15.000, 50.000], color: '#3aaa9e' },
    { id: 'japan', name: 'Japan', nameKo: lang.box.locations.japan, coordinates: [139.650, 35.676], color: '#e07040' },
    { id: 'middle-east-sa', name: 'Middle East & South America', nameKo: lang.box.locations.middleEastSa, coordinates: [30.000, 15.000], color: '#3aaa9e' },
    { id: 'north-america', name: 'North America', nameKo: lang.box.locations.northAmerica, coordinates: [-100.000, 45.000], color: '#3aaa9e' },
    { id: 'oceania', name: 'Oceania', nameKo: lang.box.locations.oceania, coordinates: [134.000, -25.000], color: '#3aaa9e' },
    { id: 'south-korea', name: 'South Korea', nameKo: lang.box.locations.southKorea, coordinates: [126.978, 37.566], color: '#e07040' },
    { id: 'southeast-asia', name: 'Southeast Asia', nameKo: lang.box.locations.southeastAsia, coordinates: [105.000, 15.000], color: '#3aaa9e' },
    { id: 'united-kingdom', name: 'United Kingdom', nameKo: lang.box.locations.unitedKingdom, coordinates: [-0.118, 51.509], color: '#e07040' },
    { id: 'united-states', name: 'United States', nameKo: lang.box.locations.unitedStates, coordinates: [-74.006, 40.713], color: '#e07040' },
];

const DELIVERY: Delivery = {
    days: 12,
    currentLocation: lang.box.pacificOcean,
};

const FROM_COLOR = '#e07040';
const TO_COLOR = '#3aaa9e';

function toLatLng(coordinates: [number, number]): [number, number] {
    return [coordinates[1], coordinates[0]];
}

function getCurveData(from: [number, number], to: [number, number], progress: number = 0.5) {
    const lat1 = from[0], lng1 = from[1];
    const lat2 = to[0], lng2 = to[1];

    let dx = lng2 - lng1;
    if (dx > 180) dx -= 360;
    else if (dx < -180) dx += 360;

    const dy = lat2 - lat1;
    const dist = Math.sqrt(dx * dx + dy * dy);

    let nLat = -dx;
    let nLng = dy;

    const nLen = Math.sqrt(nLat * nLat + nLng * nLng);
    if (nLen > 0) {
        nLat /= nLen;
        nLng /= nLen;
    }

    if (nLat < 0) {
        nLat = -nLat;
        nLng = -nLng;
    }

    const height = dist * 0.15;
    const controlPoint = [
        (lat1 + lat2) / 2 + nLat * height,
        (lng1 + lng2) / 2 + nLng * height
    ];

    const points: [number, number][] = [];
    for (let i = 0; i <= 50; i++) {
        const t = i / 50;
        const lat = (1 - t) * (1 - t) * lat1 + 2 * (1 - t) * t * controlPoint[0] + t * t * lat2;
        const lng = (1 - t) * (1 - t) * lng1 + 2 * (1 - t) * t * controlPoint[1] + t * t * lng2;
        points.push([lat, lng]);
    }

    const currentPoint: [number, number] = [
        (1 - progress) * (1 - progress) * lat1 + 2 * (1 - progress) * progress * controlPoint[0] + progress * progress * lat2,
        (1 - progress) * (1 - progress) * lng1 + 2 * (1 - progress) * progress * controlPoint[1] + progress * progress * lng2
    ];

    return { points, currentPoint };
}

function createDotIcon(color: string, label: string): L.DivIcon {
    return L.divIcon({
        className: '',
        html: `
            <div class="leaflet-city-marker">
                <div class="leaflet-city-dot-wrap" style="background:${color}40;">
                    <div class="leaflet-city-dot" style="background:${color};"></div>
                </div>
                <div class="leaflet-city-label">${label}</div>
            </div>
        `,
        iconSize: [72, 52],
        iconAnchor: [36, 8],
    });
}

function createMascotIcon(facingRight: boolean): L.DivIcon {
    return L.divIcon({
        className: 'leaflet-mascot-marker',
        html: `
            <div class="leaflet-mascot-float">
                <img
                    src="${process.env.PUBLIC_URL}/logo_image/fly1.png"
                    alt=""
                    class="leaflet-mascot-image"
                    style="transform: scaleX(${facingRight ? 1 : -1});"
                />
            </div>
        `,
        iconSize: [140, 140],
        iconAnchor: [70, 70],
    });
}

interface MapFitControllerProps {
    from: [number, number];
    to: [number, number];
    midPoint: [number, number];
}

const MapFitController: React.FC<MapFitControllerProps> = ({ from, to, midPoint }) => {
    const map = useMap();

    React.useEffect(() => {
        // PageTransition 애니메이션(400ms)이 끝난 후 지도 크기를 다시 계산하고 포커스합니다.
        // 이렇게 해야 transform(scale) 도중에 지도가 깨지거나 타일이 로드되지 않고 사라지는 문제를 방지할 수 있습니다.
        const timeoutId = setTimeout(() => {
            map.invalidateSize();
            const bounds = L.latLngBounds([from, to, midPoint]);
            map.flyToBounds(bounds, {
                padding: [90, 90],
                duration: 0.8,
                maxZoom: 7,
            });
        }, 400);
        
        return () => clearTimeout(timeoutId);
    }, [from, to, midPoint, map]);

    return null;
};

const GlobalStyle = createGlobalStyle`
    @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    html, body {
        height: 100%;
        overflow: hidden;
    }

    body {
        font-family: 'Noto Sans KR', sans-serif;
        background: #E8E0C8;
        color: #5C5040;
        -webkit-font-smoothing: antialiased;
        height: 100dvh;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    #root {
        width: 100%;
        height: 100dvh;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
    }

    @keyframes mascotFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-9px); }
    }

    .leaflet-container {
        width: 100%;
        height: 480px;
        background: #c8e8f4;
        font-family: 'Noto Sans KR', sans-serif;
        z-index: 1;
        filter: saturate(0.85) brightness(1.04) contrast(0.92);

        @media (max-width: 600px) {
            height: 360px;
        }
    }

    .leaflet-tile-pane {
        opacity: 0.9;
    }

    .leaflet-overlay-pane svg {
        filter: drop-shadow(0 2px 4px rgba(42, 124, 168, 0.18));
    }

    .leaflet-control-attribution {
        font-size: 9px;
        opacity: 0.4;
    }

    .leaflet-control-container .leaflet-top,
    .leaflet-control-container .leaflet-bottom {
        z-index: 450;
    }

    .leaflet-city-marker {
        position: relative;
        width: 72px;
        height: 52px;
        display: flex;
        flex-direction: column;
        align-items: center;
        pointer-events: auto;
    }

    .leaflet-city-dot-wrap {
        width: 16px;
        height: 16px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .leaflet-city-dot {
        width: 9px;
        height: 9px;
        border-radius: 999px;
    }

    .leaflet-city-label {
        margin-top: 7px;
        min-width: 44px;
        height: 20px;
        padding: 0 10px;
        border-radius: 999px;
        border: 0.8px solid #c8dde8;
        background: rgba(249, 246, 232, 0.95);
        color: #5C5040;
        font-family: 'Jua', sans-serif;
        font-size: 10px;
        line-height: 20px;
        text-align: center;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(90, 60, 30, 0.08);
    }

    .leaflet-mascot-marker {
        pointer-events: auto;
    }

    .leaflet-mascot-image {
        width: 140px;
        height: 140px;
        object-fit: contain;
        display: block;
        filter: drop-shadow(0 5px 10px rgba(90, 60, 30, 0.18));
    }

    .leaflet-mascot-float {
        width: 140px;
        height: 140px;
    }

    .leaflet-popup-content-wrapper {
        border-radius: 18px;
        box-shadow: 0 8px 24px rgba(90, 60, 30, 0.12);
        background: #F9F6E8;
        border: 1.5px solid #EDE9D5;
    }

    .leaflet-popup-tip {
        background: #F9F6E8;
    }

    .leaflet-popup-content {
        font-family: 'Noto Sans KR', sans-serif;
        font-size: 13px;
        color: #5C5040;
    }
`;

/* ─── Phone Shell (matches Home.tsx) ─────────────────────────────────────── */

/* ─── In-phone Navigation Bar ────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 20px 12px;
    flex-shrink: 0;
    background: rgba(249, 246, 232, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1.5px solid #EDE9D5;
`;

const BackBtn = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #EDE9D5;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s, transform 0.15s;
    color: #5C5040;

    &:hover { background: #E0D8C0; transform: scale(1.05); }
    &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
    font-family: 'Jua', sans-serif;
    font-size: 18px;
    color: #3D3020;
    flex: 1;
`;

/* ─── Scrollable content inside the phone ────────────────────────────────── */
const PhoneBody = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px 20px 32px;
    -webkit-overflow-scrolling: touch;

    /* thin custom scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #D8D0B8 transparent;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: #D8D0B8; border-radius: 4px; }
`;

const PageHeader = styled.div`
    text-align: center;
    margin-bottom: 24px;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 18px;
    background: #E07040;
    color: #fff;
    font-family: 'Jua', sans-serif;
    font-size: 13px;
    letter-spacing: 0.5px;
    border-radius: 100px;
    margin-bottom: 22px;
    box-shadow: 0 4px 12px rgba(224, 112, 64, 0.28);
`;

const PageTitle = styled.h1`
    font-family: 'Jua', sans-serif;
    font-size: 36px;
    color: #3D3020;
    margin-bottom: 12px;

    @media (max-width: 600px) { font-size: 26px; }
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #A09080;
    line-height: 1.7;
`;

const StyledMapContainer = styled.div`
    position: relative;
    border-radius: 20px;
    border: 1.5px solid #ccdee8;
    overflow: hidden;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #cce8f4 0%, #b8ddf0 100%);
    box-shadow: 0 4px 20px rgba(90, 60, 30, 0.08);

    .leaflet-container {
        width: 100%;
        height: 340px;
        background: #c8e8f4;
        font-family: 'Noto Sans KR', sans-serif;
        z-index: 1;
        filter: saturate(0.85) brightness(1.04) contrast(0.92);
    }
`;

const ArrivalCard = styled.div<{ $isExpanded: boolean; $collapsedWidth?: number }>`
    position: absolute;
    bottom: ${props => props.$isExpanded ? '0' : '20px'};
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.$isExpanded ? '100%' : (props.$collapsedWidth ? `${props.$collapsedWidth}px` : 'max-content')};
    max-width: ${props => props.$isExpanded ? '100%' : '95%'};
    height: ${props => props.$isExpanded ? '100%' : '56px'};
    background: rgba(20, 20, 20, ${props => props.$isExpanded ? '0.85' : '0.5'});
    border-radius: ${props => props.$isExpanded ? '20px' : '100px'};
    padding: ${props => props.$isExpanded ? '40px' : '0 24px'};
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    z-index: 500;
    color: #fff;
    cursor: ${props => props.$isExpanded ? 'default' : 'pointer'};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    @media (max-width: 600px) {
        padding: ${props => props.$isExpanded ? '30px 20px' : '0 16px'};
        border-radius: ${props => props.$isExpanded ? '16px' : '100px'};
    }
`;

const CollapsedContent = styled.div<{ $isExpanded: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    height: 100%;
    opacity: ${props => props.$isExpanded ? 0 : 1};
    transition: opacity 0.2s;
    position: ${props => props.$isExpanded ? 'absolute' : 'relative'};
    pointer-events: ${props => props.$isExpanded ? 'none' : 'auto'};
    white-space: nowrap;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 24px;
    right: 24px;
    z-index: 20;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: background 0.2s;
    &:hover {
        background: rgba(255, 255, 255, 0.4);
    }

    @media (max-width: 600px) {
        top: 16px;
        right: 16px;
    }
`;

const ExpandedContent = styled.div<{ $isExpanded: boolean }>`
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 20px 20px;
    gap: 16px;
    text-align: center;
    opacity: ${props => props.$isExpanded ? 1 : 0};
    transition: opacity 0.3s 0.2s;
    position: ${props => props.$isExpanded ? 'relative' : 'absolute'};
    pointer-events: ${props => props.$isExpanded ? 'auto' : 'none'};
    overflow-y: auto;
`;

const ExpandedLeft = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const ExpandedTitle = styled.div`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
`;

const ExpandedStatus = styled.div`
    font-family: 'Jua', sans-serif;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
    word-break: keep-all;
`;

const ExpandedDesc = styled.div`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.65;
    word-break: keep-all;
    max-width: 340px;
`;

const ExpandedRight = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    img {
        width: 140px;
        height: 140px;
        object-fit: contain;
        border-radius: 16px;
    }
`;

const ArrivalIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #007aff;
    color: #fff;
    flex-shrink: 0;
`;

const ArrivalInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ArrivalLabel = styled.p`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 600;
    letter-spacing: 0.3px;
`;

const ArrivalDays = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.3px;
    font-family: 'Noto Sans KR', sans-serif;

    @media (max-width: 600px) {
        font-size: 14px;
    }
`;

const ArrivalSub = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    @media (max-width: 600px) {
        gap: 8px;
    }
`;

const ArrivalStatus = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    display: block;
    white-space: nowrap;

    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

const ArrivalDuration = styled.span`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    padding-left: 12px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);

    @media (max-width: 600px) {
        font-size: 11px;
        padding-left: 8px;
    }
`;

const PeopleRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 40px;

    @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const PersonCard = styled.div`
    border: 1.5px solid #EDE9D5;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(90, 60, 30, 0.06);
`;

const PersonCardHeader = styled.div`
    padding: 12px 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #EDE9D5;
    background: #F0ECD8;
`;

const PersonCardTitle = styled.span`
    font-family: 'Jua', sans-serif;
    font-size: 13px;
    color: #8A7A60;
    font-weight: 400;
`;

const PersonCardBody = styled.div`
    padding: 20px;
    background: #FFF;
`;

const PersonInfo = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
`;

const PersonRole = styled.span`
    font-family: 'Jua', sans-serif;
    font-size: 14px;
    color: #8A7A60;
`;

const PersonName = styled.p`
    font-family: 'Jua', sans-serif;
    font-size: 22px;
    color: #3D3020;
`;

const LocationText = styled.span`
    font-size: 14px;
    color: #8A7A60;
    font-weight: 500;
`;

const CtaWrap = styled.div`
    display: flex;
    justify-content: center;
`;

const CtaButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 15px 36px;
    background: #E07040;
    color: #fff;
    border: none;
    border-radius: 100px;
    font-family: 'Jua', sans-serif;
    font-size: 15px;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 6px 18px rgba(224, 112, 64, 0.35);

    &:hover { background: #C85E30; transform: translateY(-2px); box-shadow: 0 10px 24px rgba(224, 112, 64, 0.4); }
    &:active { transform: translateY(0); }
`;

const Box: React.FC = () => {
    const [fromCity, setFromCity] = useState<LocationOption>(LOCATION_OPTIONS[7]); // 한국
    const [toCity, setToCity] = useState<LocationOption>(LOCATION_OPTIONS[1]); // 동아시아

    const [senderName, setSenderName] = useState<string>('');
    const [receiverName, setReceiverName] = useState<string>('');

    const [deliveryStatus, setDeliveryStatus] = useState<string>('경로 탐색 중...');
    const [sentAtState, setSentAtState] = useState<number>(0);
    const [receivedAtState, setReceivedAtState] = useState<number>(0);
    const [nowTime, setNowTime] = useState(Date.now());
    React.useEffect(() => {
        const timer = setInterval(() => setNowTime(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [delayType, setDelayType] = useState<'normal' | 'normal_transit' | 'normal_clearance' | 'normal_delivery' | 'transit' | 'clearance' | 'delivery' | 'error'>('normal');
    const [transportType, setTransportType] = useState<'air' | 'ship'>('air');
    const [noMail, setNoMail] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [activeMailId, setActiveMailId] = useState<string | null>(null);

    const getRandomName = (locationId: string) => {
        if (locationId === 'south-korea') {
            const names = (koKr as any).names?.['south-korea'] || ['지민'];
            return names[Math.floor(Math.random() * names.length)];
        } else {
            const names = (enEng as any).names?.[locationId] || ['John'];
            return names[Math.floor(Math.random() * names.length)];
        }
    };

    const [mailData, setMailData] = useState<any>(null);

    React.useEffect(() => {
        let isMounted = true;
        const initData = async () => {
            const params = new URLSearchParams(window.location.search);
            let mailId = params.get('mail_id');

            setDeliveryStatus(lang.box.routing);

            try {
                const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                
                if (!mailId) {
                    const sentRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/mail/sent`, {
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                    });
                    if (sentRes.ok) {
                        const sentMails = await sentRes.json();
                        const inTransitMails = (sentMails || []).filter((m: any) => {
                            const sentAt = parseDate(m.sent_at);
                            // Priority: estimated_arrival_at > remaining_minutes > received_at > fallback
                            let receivedAt = 0;
                            if (m.estimated_arrival_at) {
                                receivedAt = new Date(m.estimated_arrival_at).getTime();
                            } else if (m.remaining_minutes !== undefined && m.remaining_minutes !== null && m.remaining_minutes !== "") {
                                receivedAt = Date.now() + Number(m.remaining_minutes) * 60000;
                            } else {
                                const rawRAt = m.received_at ? parseDate(m.received_at) : 0;
                                if (rawRAt && Math.abs(rawRAt - sentAt) >= 60000) {
                                    receivedAt = rawRAt;
                                } else {
                                    const fallbackMins = (!isNaN(Number(m.send_type)) && Number(m.send_type) > 0) ? Number(m.send_type) : 8640;
                                    receivedAt = sentAt + ((Number(m.arrival_delay_minutes) > 0 ? Number(m.arrival_delay_minutes) : fallbackMins) * 60000);
                                }
                            }
                            return Date.now() < receivedAt;
                        });
                        
                        if (inTransitMails.length > 0) {
                            const sortedMails = inTransitMails.sort((a: any, b: any) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
                            mailId = sortedMails[0].id.toString();
                        } else {
                            if (isMounted) {
                                setNoMail(true);
                                setIsLoading(false);
                            }
                            return;
                        }
                    } else {
                        if (isMounted) {
                            setNoMail(true);
                            setIsLoading(false);
                        }
                        return;
                    }
                }

                const mailRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/mail/${mailId}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                
                if (!mailRes.ok) throw new Error('Failed to fetch mail');
                const mail = await mailRes.json();
                
                if (!isMounted) return;
                setActiveMailId(mailId);
                setMailData(mail);
                setSenderName(mail.user?.user_alias || mail.user?.user_name || mail.sender_email);
                setReceiverName(mail.receiver?.user_alias || mail.receiver?.user_name || mail.receiver_email);

                const sentAt = parseDate(mail.sent_at);
                // Priority: estimated_arrival_at > remaining_minutes > received_at > fallback
                let receivedAt = 0;
                if (mail.estimated_arrival_at) {
                    receivedAt = new Date(mail.estimated_arrival_at).getTime();
                } else if (mail.remaining_minutes !== undefined && mail.remaining_minutes !== null && mail.remaining_minutes !== "") {
                    receivedAt = Date.now() + Number(mail.remaining_minutes) * 60000;
                } else {
                    const rawRAt = mail.received_at ? parseDate(mail.received_at) : 0;
                    if (rawRAt && Math.abs(rawRAt - sentAt) >= 60000) {
                        receivedAt = rawRAt;
                    } else {
                        const fallbackMins = (!isNaN(Number(mail.send_type)) && Number(mail.send_type) > 0) ? Number(mail.send_type) : 8640;
                        receivedAt = sentAt + ((Number(mail.arrival_delay_minutes) > 0 ? Number(mail.arrival_delay_minutes) : fallbackMins) * 60000);
                    }
                }

                const totalDays = (receivedAt - sentAt) / (1000 * 60 * 60 * 24);
                let matchedDest = LOCATION_OPTIONS[1];
                if (totalDays <= 1) matchedDest = LOCATION_OPTIONS.find(o => o.id === 'south-korea') || LOCATION_OPTIONS[7];
                else if (totalDays <= 2) matchedDest = LOCATION_OPTIONS.find(o => o.id === 'japan') || LOCATION_OPTIONS[3];
                else if (totalDays <= 4) matchedDest = LOCATION_OPTIONS.find(o => o.id === 'southeast-asia') || LOCATION_OPTIONS[8];
                else matchedDest = LOCATION_OPTIONS.find(o => o.id === 'north-america') || LOCATION_OPTIONS[5];

                const originCity = LOCATION_OPTIONS.find(o => o.id === 'south-korea') || LOCATION_OPTIONS[7];
                setFromCity(originCity);
                setToCity(matchedDest);

                // Fetch logistics for risk & transport type
                const logRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/route-logistics?origin=${encodeURIComponent(originCity.name)}&destination=${encodeURIComponent(matchedDest.name)}`);
                if (logRes.ok) {
                    const data = await logRes.json();
                    if (data.success !== false) {
                        let type: 'air' | 'ship' = 'air';
                        if (data.service_type) {
                            const sType = data.service_type.toLowerCase();
                            if (sType.includes('sea') || sType.includes('ocean') || sType.includes('ship')) type = 'ship';
                        }
                        setTransportType(type);
                    }
                }

                // Risk info evaluation is moved to a separate dynamic useEffect
                // to correctly handle the 1/3 progression rule.

                setSentAtState(sentAt);
                setReceivedAtState(receivedAt);
                
                if (isMounted) setIsLoading(false);

            } catch (err) {
                if (isMounted) {
                    setIsLoading(false);
                    if (!mailId) {
                        setNoMail(true);
                    } else {
                        setDeliveryStatus(lang.box.errorTitle);
                        setDelayType('error');
                    }
                }
            }
        };

        initData();
        return () => { isMounted = false; };
    }, []);

    // 배송 위험 정보와 도착 예정 시각은 서버에서 바뀔 수 있으므로 주기적으로 갱신합니다.
    React.useEffect(() => {
        if (!activeMailId) return;

        let isMounted = true;
        const refreshMailStatus = async () => {
            try {
                const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/mail/${activeMailId}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                    cache: 'no-store',
                });
                if (!response.ok) return;

                const nextMail = await response.json();
                if (!isMounted) return;

                const sentAt = parseDate(nextMail.sent_at);
                let receivedAt = 0;
                if (nextMail.estimated_arrival_at) {
                    receivedAt = new Date(nextMail.estimated_arrival_at).getTime();
                } else if (nextMail.remaining_minutes !== undefined && nextMail.remaining_minutes !== null && nextMail.remaining_minutes !== '') {
                    receivedAt = Date.now() + Number(nextMail.remaining_minutes) * 60000;
                } else {
                    const rawReceivedAt = nextMail.received_at ? parseDate(nextMail.received_at) : 0;
                    if (rawReceivedAt && Math.abs(rawReceivedAt - sentAt) >= 60000) {
                        receivedAt = rawReceivedAt;
                    } else {
                        const fallbackMinutes = (!isNaN(Number(nextMail.send_type)) && Number(nextMail.send_type) > 0)
                            ? Number(nextMail.send_type)
                            : 8640;
                        receivedAt = sentAt + ((Number(nextMail.arrival_delay_minutes) > 0
                            ? Number(nextMail.arrival_delay_minutes)
                            : fallbackMinutes) * 60000);
                    }
                }

                setMailData(nextMail);
                setSenderName(nextMail.user?.user_alias || nextMail.user?.user_name || nextMail.sender_email);
                setReceiverName(nextMail.receiver?.user_alias || nextMail.receiver?.user_name || nextMail.receiver_email);
                setSentAtState(sentAt);
                setReceivedAtState(receivedAt);
                setNowTime(Date.now());
            } catch {
                // 일시적인 네트워크 오류에는 현재 표시값을 유지하고 다음 주기에 다시 시도합니다.
            }
        };

        const intervalId = window.setInterval(refreshMailStatus, 15000);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshMailStatus();
        };
        window.addEventListener('focus', refreshMailStatus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', refreshMailStatus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeMailId]);

    React.useEffect(() => {
        if (mailData && sentAtState > 0 && receivedAtState > 0) {
            let currentProgress = 0;
            if (nowTime >= receivedAtState) currentProgress = 1;
            else if (nowTime <= sentAtState) currentProgress = 0;
            else currentProgress = (nowTime - sentAtState) / (receivedAtState - sentAtState);

            const transitRisk = mailData.transit_risk || mailData.transitRisk || '';
            const clearanceRisk = mailData.clearance_risk || mailData.clearanceRisk || '';
            const deliveryRisk = mailData.delivery_risk || mailData.deliveryRisk || '';

            const isNormalRisk = (s: string) => !s || s.trim() === '정상 배송 (지연 없음)' || s.trim() === '' || s.includes('정상 배송');

            if (currentProgress >= 1) {
                setDeliveryStatus((lang.box as any).statusDelivered || lang.box.statusDelivered);
                setDelayType('normal');
            } else if (currentProgress < 0.333) {
                if (!isNormalRisk(transitRisk)) {
                    setDeliveryStatus(transitRisk);
                    setDelayType('transit');
                } else {
                    setDeliveryStatus((lang.box as any).preparing || lang.box.normalDelivery);
                    setDelayType('normal');
                }
            } else if (currentProgress < 0.666) {
                if (!isNormalRisk(clearanceRisk)) {
                    setDeliveryStatus(clearanceRisk);
                    setDelayType('clearance');
                } else {
                    setDeliveryStatus((lang.box as any).clearance || lang.box.normalDelivery);
                    setDelayType('normal');
                }
            } else {
                if (!isNormalRisk(deliveryRisk)) {
                    setDeliveryStatus(deliveryRisk);
                    setDelayType('delivery');
                } else {
                    setDeliveryStatus((lang.box as any).domestic || lang.box.normalDelivery);
                    setDelayType('normal');
                }
            }
        }
    }, [nowTime, sentAtState, receivedAtState, mailData]);

    const fromLatLng = useMemo(() => toLatLng(fromCity.coordinates), [fromCity]);
    const toLatLngPoint = useMemo(() => toLatLng(toCity.coordinates), [toCity]);

    // Calculate dynamically for real-time updates
    let progress = 0.5;
    let deliveryDays = '-';
    let deliveryDate = '-';
    if (sentAtState > 0 && receivedAtState > 0) {
        if (nowTime >= receivedAtState) progress = 1;
        else if (nowTime <= sentAtState) progress = 0;
        else progress = (nowTime - sentAtState) / (receivedAtState - sentAtState);
        
        const remainingMins = Math.max(0, Math.round((receivedAtState - nowTime) / 60000));
        
        // Show remaining time instead of total time to tick down dynamically
        const d = Math.floor(remainingMins / 1440);
        const h = Math.floor((remainingMins % 1440) / 60);
        const m = Math.floor(remainingMins % 60);
        const parts = [];
        if (d > 0) parts.push(`${d}${lang.box.main?.days || '일'}`);
        if (h > 0) parts.push(`${h}${lang.box.main?.hours || '시간'}`);
        if ((m > 0 || remainingMins === 0) && d === 0) parts.push(`${m}${lang.box.main?.minutes || '분'}`);
        deliveryDays = parts.length > 0 ? parts.join(' ') : '-';
        
        const rDate = new Date(receivedAtState);
        deliveryDate = `${rDate.getMonth() + 1}월 ${rDate.getDate()}일`;
    }

    const { points: curvePoints, currentPoint: mascotLatLng } = useMemo(
        () => getCurveData(fromLatLng, toLatLngPoint, progress),
        [fromLatLng, toLatLngPoint, progress]
    );
    const fromIcon = useMemo(() => createDotIcon(FROM_COLOR, fromCity.nameKo), [fromCity.nameKo]);
    const toIcon = useMemo(() => createDotIcon(TO_COLOR, toCity.nameKo), [toCity.nameKo]);
    const mascotFacesRight = toLatLngPoint[1] >= fromLatLng[1];
    const mascotIcon = useMemo(() => createMascotIcon(mascotFacesRight), [mascotFacesRight]);

    const mapCenter = mascotLatLng;

    const [exactCollapsedWidth, setExactCollapsedWidth] = useState<number | undefined>(undefined);
    const measureRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (measureRef.current) {
            setExactCollapsedWidth(measureRef.current.offsetWidth);
        }
    }, [deliveryStatus, deliveryDays, deliveryDate]);



    if (isLoading) {
        return (
            <>
                <GlobalStyle />
                <ResponsiveShell>
                    <PhoneNavBar>
                        <BackBtn
                            onClick={() => {
                                if (window.history.length > 2) {
                                    window.history.back();
                                } else {
                                    window.location.href = '/letterbox';
                                }
                            }}
                            aria-label="뒤로가기"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                        </BackBtn>
                        <NavTitle>{lang.box.pageTitle}</NavTitle>
                    </PhoneNavBar>
                    <PhoneBody style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <div style={{ textAlign: 'center', color: '#B0A090' }}>
                            <div style={{ fontFamily: 'Jua, sans-serif', fontSize: '20px' }}>
                                {lang.box.routing || "경로 탐색 중..."}
                            </div>
                        </div>
                    </PhoneBody>
                </ResponsiveShell>
            </>
        );
    }

    if (noMail) {
        return (
            <>
                <GlobalStyle />
                <ResponsiveShell>
                    <PhoneNavBar>
                        <BackBtn
                            onClick={() => {
                                if (window.history.length > 2) {
                                    window.history.back();
                                } else {
                                    window.location.href = '/';
                                }
                            }}
                            aria-label="뒤로가기"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                        </BackBtn>
                        <NavTitle>{lang.box.pageTitle}</NavTitle>
                    </PhoneNavBar>
                    <PhoneBody style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <div style={{ textAlign: 'center', color: '#B0A090' }}>
                            <div style={{ fontFamily: 'Jua, sans-serif', fontSize: '20px' }}>
                                {lang.box.emptyMail}
                            </div>
                        </div>
                    </PhoneBody>
                </ResponsiveShell>
            </>
        );
    }

    return (
        <>
            <GlobalStyle />
            <ResponsiveShell>
                <PhoneNavBar>
                    <BackBtn
                        onClick={() => {
                            if (window.history.length > 2) {
                                window.history.back();
                            } else {
                                window.location.href = '/letterbox';
                            }
                        }}
                        aria-label="뒤로가기"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </BackBtn>
                    <NavTitle>{lang.letterBox.title}</NavTitle>
                </PhoneNavBar>

                <PhoneBody>
                    <PageHeader>
                        <StatusBadge>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13" rx="2" />
                                <path d="M16 8h4l3 3v5h-7V8z" />
                                <circle cx="5.5" cy="18.5" r="2.5" />
                                <circle cx="18.5" cy="18.5" r="2.5" />
                            </svg>
                            {lang.box.statusDelivering}
                        </StatusBadge>
                        <PageTitle>{lang.box.pageTitle}</PageTitle>
                        <PageDesc>{lang.box.pageDesc}</PageDesc>
                    </PageHeader>

                    <StyledMapContainer>
                        <LeafletMapContainer
                            center={mapCenter}
                            zoom={4}
                            minZoom={2}
                            maxZoom={10}
                            scrollWheelZoom={false}
                            dragging={true}
                            zoomControl={false}
                            attributionControl={true}
                        >
                            <MapFitController from={fromLatLng} to={toLatLngPoint} midPoint={mascotLatLng} />

                            <TileLayer
                                attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                                subdomains={['a', 'b', 'c', 'd']}
                            />

                            <Polyline
                                key={`${fromCity.id}-${toCity.id}`}
                                positions={curvePoints}
                                pathOptions={{
                                    color: '#4f9fc4',
                                    weight: 4,
                                    dashArray: '8 8',
                                    opacity: 0.95,
                                    lineCap: 'round',
                                    lineJoin: 'round',
                                }}
                            />

                            <CircleMarker
                                center={fromLatLng}
                                radius={7}
                                interactive={false}
                                pathOptions={{
                                    color: '#FFFDF5',
                                    weight: 3,
                                    fillColor: FROM_COLOR,
                                    fillOpacity: 1,
                                }}
                            />

                            <CircleMarker
                                center={toLatLngPoint}
                                radius={7}
                                interactive={false}
                                pathOptions={{
                                    color: '#FFFDF5',
                                    weight: 3,
                                    fillColor: TO_COLOR,
                                    fillOpacity: 1,
                                }}
                            />

                            <Marker position={fromLatLng} icon={fromIcon}>
                                <Popup>{fromCity.nameKo} ({fromCity.name})</Popup>
                            </Marker>

                            <Marker position={toLatLngPoint} icon={toIcon}>
                                <Popup>{toCity.nameKo} ({toCity.name})</Popup>
                            </Marker>

                            <Marker position={mascotLatLng} icon={mascotIcon} zIndexOffset={1000}>
                                <Popup>{deliveryStatus}</Popup>
                            </Marker>
                        </LeafletMapContainer>


                        <div
                            ref={measureRef}
                            style={{
                                position: 'absolute',
                                visibility: 'hidden',
                                pointerEvents: 'none',
                                width: 'max-content',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                padding: '10px 24px',
                                boxSizing: 'border-box'
                            }}
                        >
                            <ArrivalIconWrap>
                                {transportType === 'ship' ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20,21C18.61,21 17.22,20.53 16,19.67C14.78,20.53 13.39,21 12,21C10.61,21 9.22,20.53 8,19.67C6.78,20.53 5.39,21 4,21H2V19.66C3.25,19.66 4.46,19.26 5.5,18.53C6.5,19.26 7.75,19.66 9,19.66C10.25,19.66 11.5,19.26 12.5,18.53C13.5,19.26 14.75,19.66 16,19.66C17.25,19.66 18.5,19.26 19.5,18.53C20.5,19.26 21.75,19.66 23,19.66V21H20M4.33,16H19.67L20.89,12H3.11L4.33,16M6,6H18V10H6V6M9,2H15V4H9V2Z" />
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" />
                                    </svg>
                                )}
                            </ArrivalIconWrap>
                            <ArrivalInfo>
                                <ArrivalLabel>{lang.box.etaLabel}</ArrivalLabel>
                                <ArrivalDays>{deliveryDate === '-' ? '-' : deliveryDate}</ArrivalDays>
                            </ArrivalInfo>
                            <ArrivalSub>
                                <ArrivalStatus>{deliveryStatus}</ArrivalStatus>
                                <ArrivalDuration>| {deliveryDays === '-' ? '-' : deliveryDays} {lang.box.durationSuffix}</ArrivalDuration>
                            </ArrivalSub>
                        </div>

                        <ArrivalCard $isExpanded={isExpanded} $collapsedWidth={exactCollapsedWidth} onClick={() => !isExpanded && setIsExpanded(true)}>
                            <CloseButton
                                style={{ opacity: isExpanded ? 1 : 0, pointerEvents: isExpanded ? 'auto' : 'none', transition: 'opacity 0.2s' }}
                                onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </CloseButton>

                            <CollapsedContent $isExpanded={isExpanded}>
                                <ArrivalIconWrap>
                                    {transportType === 'ship' ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20,21C18.61,21 17.22,20.53 16,19.67C14.78,20.53 13.39,21 12,21C10.61,21 9.22,20.53 8,19.67C6.78,20.53 5.39,21 4,21H2V19.66C3.25,19.66 4.46,19.26 5.5,18.53C6.5,19.26 7.75,19.66 9,19.66C10.25,19.66 11.5,19.26 12.5,18.53C13.5,19.26 14.75,19.66 16,19.66C17.25,19.66 18.5,19.26 19.5,18.53C20.5,19.26 21.75,19.66 23,19.66V21H20M4.33,16H19.67L20.89,12H3.11L4.33,16M6,6H18V10H6V6M9,2H15V4H9V2Z" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" />
                                        </svg>
                                    )}
                                </ArrivalIconWrap>

                                <ArrivalInfo>
                                    <ArrivalLabel>{lang.box.etaLabel}</ArrivalLabel>
                                    <ArrivalDays>{deliveryDate === '-' ? '-' : deliveryDate}</ArrivalDays>
                                </ArrivalInfo>

                                <ArrivalSub>
                                    <ArrivalStatus>{deliveryStatus}</ArrivalStatus>
                                    <ArrivalDuration>{deliveryDays === '-' ? '-' : deliveryDays} {lang.box.durationSuffix}</ArrivalDuration>
                                </ArrivalSub>
                            </CollapsedContent>

                            <ExpandedContent $isExpanded={isExpanded}>
                                <ExpandedRight>
                                    <img src={`${process.env.PUBLIC_URL}/logo_image/${
                                        delayType === 'clearance' || delayType === 'normal_clearance' ? 'dojang.png' :
                                        delayType === 'transit' ? 'sea.png' :
                                        delayType === 'delivery' ? 'error2.png' :
                                        delayType === 'error' ? 'error.png' :
                                        'fly1.png'
                                        }`} alt="status" style={
                                            delayType === 'clearance' || delayType === 'normal_clearance' ||
                                            delayType === 'transit' || delayType === 'delivery' || delayType === 'error'
                                                ? undefined
                                                : { transform: `scaleX(${mascotFacesRight ? 1 : -1})` }
                                        } />
                                </ExpandedRight>
                                <ExpandedLeft>
                                    <ExpandedTitle>{lang.box.expandedTitle}</ExpandedTitle>
                                    <ExpandedStatus>
                                        {deliveryStatus.split(' (').map((part, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && <br />}
                                                {i > 0 ? `(${part}` : part}
                                            </React.Fragment>
                                        ))}
                                    </ExpandedStatus>
                                    <ExpandedDesc>
                                        {delayType === 'normal' && lang.box.descNormal}
                                        {delayType === 'normal_transit' && (lang.box.transitDesc || lang.box.descNormal)}
                                        {delayType === 'normal_clearance' && ((lang.box as any).clearanceDesc || lang.box.descNormal)}
                                        {delayType === 'normal_delivery' && ((lang.box as any).domesticDesc || lang.box.descNormal)}
                                        {delayType === 'clearance' && lang.box.descClearance}
                                        {delayType === 'transit' && lang.box.descTransit}
                                        {delayType === 'delivery' && lang.box.descDelivery}
                                        {delayType === 'error' && lang.box.descError}
                                    </ExpandedDesc>
                                </ExpandedLeft>
                            </ExpandedContent>

                        </ArrivalCard>
                    </StyledMapContainer>

                    <PeopleRow>
                        <PersonCard>
                            <PersonCardHeader>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                                <PersonCardTitle>{lang.box.senderLabel}</PersonCardTitle>
                            </PersonCardHeader>
                            <PersonCardBody>
                                <PersonInfo>
                                    <PersonRole>{lang.box.sender}</PersonRole>
                                    <PersonName>{senderName}</PersonName>
                                </PersonInfo>
                                <LocationText>{fromCity.nameKo}</LocationText>
                            </PersonCardBody>
                        </PersonCard>

                        <PersonCard>
                            <PersonCardHeader>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="4" width="20" height="16" rx="3" />
                                    <path d="m22 7-10 7L2 7" />
                                </svg>
                                <PersonCardTitle>{lang.box.receiverLabel}</PersonCardTitle>
                            </PersonCardHeader>
                            <PersonCardBody>
                                <PersonInfo>
                                    <PersonRole>{lang.box.receiver}</PersonRole>
                                    <PersonName>{receiverName}</PersonName>
                                </PersonInfo>
                                <LocationText>{toCity.nameKo}</LocationText>
                            </PersonCardBody>
                        </PersonCard>
                    </PeopleRow>

                    <CtaWrap>
                        <CtaButton>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                            {lang.box.notifyArrival}
                        </CtaButton>
                    </CtaWrap>
                </PhoneBody>
            </ResponsiveShell>
        </>
    );
};

export default Box;
